import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Github, RefreshCw, ExternalLink, Check, AlertCircle, Lock } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function PushToGitHubDialog({ open, onOpenChange, blueprint, platforms = ["ios"] }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priv, setPriv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {repo_url, actions_url, commit_sha}
  const [needsConnect, setNeedsConnect] = useState(false);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setNeedsConnect(false);
    const t = (blueprint?.title || "my-app").replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9._-]+/g, "");
    setName(t || "my-apple-app");
    setDescription(blueprint?.concept?.slice(0, 240) || "Generated by Durga Programming Adventures");
    api.get("/github/me").then(({ data }) => {
      if (!data.connected) setNeedsConnect(true);
    }).catch(() => setNeedsConnect(true));
  }, [open, blueprint]);

  const push = async () => {
    if (!name.trim()) return toast.error("Repo name is required");
    setBusy(true);
    try {
      const { data } = await api.post("/github/push", {
        blueprint,
        platforms,
        marketing: false,
        repo_name: name.trim(),
        description,
        private: priv,
      });
      setResult(data);
      toast.success("Pushed to GitHub");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Push failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream border-2 border-ink rounded-3xl max-w-xl" data-testid="push-github-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
            <Github className="w-5 h-5 text-marigold" /> Push to GitHub
          </DialogTitle>
          <DialogDescription className="text-ink2">
            Creates a brand-new repo in your account and commits the multi-platform Apple starter kit (with the
            GitHub Actions workflow that builds the .dmg automatically).
          </DialogDescription>
        </DialogHeader>

        {needsConnect ? (
          <div className="mt-4 card-soft p-5 text-center" data-testid="push-needs-connect">
            <AlertCircle className="w-6 h-6 text-marigold mx-auto mb-2" />
            <div className="font-semibold mb-1">Connect GitHub first</div>
            <div className="text-sm text-ink2 mb-3">Head to your dashboard to link your GitHub account.</div>
            <a href="/dashboard" className="btn-primary text-sm py-2 inline-flex">
              <Github className="w-4 h-4" /> Go to dashboard
            </a>
          </div>
        ) : result ? (
          <div className="mt-4 space-y-3" data-testid="push-success">
            <div className="card-soft p-4 inline-flex items-center gap-2 text-teal font-medium">
              <Check className="w-5 h-5" /> Repo created and pushed
            </div>
            <div className="text-sm space-y-1.5">
              <a href={result.repo_url} target="_blank" rel="noreferrer" className="block text-marigold hover:underline">
                <ExternalLink className="w-3.5 h-3.5 inline mr-1" /> {result.repo_url}
              </a>
              <a href={result.actions_url} target="_blank" rel="noreferrer" className="block text-ink2 hover:text-ink hover:underline">
                <ExternalLink className="w-3.5 h-3.5 inline mr-1" /> Actions tab — your .dmg will be built here
              </a>
              <div className="text-ink2 font-mono text-xs">commit {result.commit_sha?.slice(0, 7)}</div>
            </div>
          </div>
        ) : (
          <div className="space-y-3 mt-4">
            <div>
              <div className="label-eyebrow mb-1">Repo name</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full card-soft p-2.5 text-sm font-mono bg-transparent outline-none"
                data-testid="push-repo-name"
              />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Description</div>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full card-soft p-2.5 text-sm bg-transparent outline-none"
                data-testid="push-repo-desc"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="push-repo-private">
              <input type="checkbox" checked={priv} onChange={(e) => setPriv(e.target.checked)} className="accent-marigold" />
              <Lock className="w-3.5 h-3.5 text-ink2" /> Make this repo private
            </label>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button
                onClick={() => onOpenChange(false)}
                className="text-sm px-4 py-2 rounded-full border border-border text-ink2 hover:border-ink hover:text-ink"
                data-testid="push-cancel"
              >
                Cancel
              </button>
              <button onClick={push} disabled={busy} className="btn-primary text-sm py-2 disabled:opacity-40" data-testid="push-submit">
                {busy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Pushing…</> : <><Github className="w-4 h-4" /> Create repo & push</>}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
